import React, { useEffect, useState, FormEvent, KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { jwtDecode } from 'jwt-decode';
import { useStore } from '@store/StoreProvider';
import { postAuthLandingPath } from '@constants/adminNav';
import { toastStore } from '@store/ToastStore';
import {
  applyEmailNotVerifiedRestriction,
  readEmailVerifiedFlag,
} from '@utils/apiHelpers';
import { EMAIL_VERIFICATION_PROFILE_PATH } from '@store/EmailVerificationStore';
import { AuthPageShell } from '@app/auth/AuthPageShell';
import { RecaptchaNotice } from '@app/auth/RecaptchaNotice';
import { PasswordToggle } from '@app/auth/authIcons';
import { fetchWithRecaptchaRetry, preloadRecaptcha } from '@utils/recaptcha';

const PASSWORD_RESET_SUCCESS_KEY = 'auth.passwordResetSuccessMessage';

function loginReasonBanner(reason: string | null, t: (key: string) => string, stored?: string | null): string | null {
  if (reason === 'blocked') {
    const fromI18n = t('errors.auth.account_blocked')
    const fallback = fromI18n === 'errors.auth.account_blocked' ? 'Аккаунт заблокирован.' : fromI18n
    return stored?.trim() || fallback
  }
  if (reason === 'session-reset') {
    const fromI18n = t('errors.auth.session_reset')
    const fallback = fromI18n === 'errors.auth.session_reset' ? 'Сессия была сброшена. Войдите снова.' : fromI18n
    return stored?.trim() || fallback
  }
  if (reason === 'expired') return sessionExpiredText(t)
  return null
}

/** JWT ran out (401 TOKEN_EXPIRED or client-side exp): calm, human, in the UI language. */
function sessionExpiredText(t: (key: string) => string): string {
  const fromI18n = t('auth.session.expired')
  if (fromI18n && fromI18n !== 'auth.session.expired') return fromI18n
  const ru = typeof document !== 'undefined' && (document.documentElement.lang || '').toLowerCase().startsWith('ru')
  return ru
    ? 'Время сессии истекло. Войдите снова, чтобы продолжить работу.'
    : 'Your session has expired. Sign in again to continue.'
}

export default observer(function LoginPage() {
  const { i18n, auth } = useStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState<string | null>(null);

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  useEffect(() => {
    try {
      const resetMsg = sessionStorage.getItem(PASSWORD_RESET_SUCCESS_KEY);
      if (resetMsg?.trim()) {
        sessionStorage.removeItem(PASSWORD_RESET_SUCCESS_KEY);
        toastStore.show(resetMsg.trim(), 'success', 5000);
      }

      const stored = sessionStorage.getItem('auth.sessionExpiredMessage');
      if (stored) sessionStorage.removeItem('auth.sessionExpiredMessage');

      const fromQuery = loginReasonBanner(searchParams.get('reason'), (key) => i18n.t(key), stored);
      if (fromQuery) {
        setSessionExpiredBanner(fromQuery);
        return;
      }

      if (!stored) return;
      if (stored.trim() === 'auth.session.replaced' || stored.includes('Начата новая сессия')) {
        setSessionExpiredBanner(i18n.t('auth.session.replaced'));
        return;
      }
      if (stored.trim() === 'auth.session.expired' || /token expired|jwt expired/i.test(stored)) {
        setSessionExpiredBanner(sessionExpiredText((key) => i18n.t(key)));
        return;
      }
      toastStore.show(stored, 'error', 6000);
    } catch {}
  }, [i18n, searchParams]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    if (auth.currentUser?.emailVerified === false) {
      navigate(EMAIL_VERIFICATION_PROFILE_PATH, { replace: true });
      return;
    }
    navigate(postAuthLandingPath(auth.currentUser?.role), { replace: true });
  }, [auth.isAuthenticated, auth.currentUser?.emailVerified, auth.currentUser?.role, navigate]);

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validatePasswordStrength = (value: string) => {
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
    };
  };

  const handleFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter' || e.repeat || e.nativeEvent.isComposing) return;
    if (!(e.target instanceof HTMLInputElement)) return;
    e.preventDefault();
    if (isLoading) return;
    e.currentTarget.requestSubmit();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    let isValid = true;
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      newErrors.email = i18n.t('errors.auth.enter_email');
      isValid = false;
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = i18n.t('errors.auth.invalid_email');
      isValid = false;
    }

    if (!password) {
      newErrors.password = i18n.t('errors.auth.password_required');
      isValid = false;
    } else if (password.length < 8) {
      newErrors.password = i18n.t('errors.auth.password_min_length');
      isValid = false;
    } else {
      const passwordCheck = validatePasswordStrength(password);
      if (!passwordCheck.isValid) {
        const missing = [];
        if (!passwordCheck.hasUpperCase) missing.push(i18n.t('auth.password_rules.uppercase'));
        if (!passwordCheck.hasLowerCase) missing.push(i18n.t('auth.password_rules.lowercase'));
        if (!passwordCheck.hasNumber) missing.push(i18n.t('auth.password_rules.digit'));
        newErrors.password = i18n.t('auth.password_rules.must_contain', { rules: missing.join(', ') });
        isValid = false;
      }
    }

    if (!isValid) {
      setErrors(newErrors);
      toastStore.show(i18n.t('errors.fix_form'), 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithRecaptchaRetry('login', (captchaToken) =>
        fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmedEmail, password, captchaToken }),
        }),
      );

      const data = await response.json();
      if (response.ok) {
        if (data.token) auth.setToken(data.token);
        const user: { id: number; email: string; role: string; iat: string } = jwtDecode(data.token);
        const emailVerified = readEmailVerifiedFlag(data);
        auth.setUser({
          id: user.id,
          email: trimmedEmail,
          role: user.role,
          createdAt: user.iat,
          ...(emailVerified !== undefined ? { emailVerified } : {}),
        });
        setEmail('');
        setPassword('');
        if (emailVerified === false) {
          applyEmailNotVerifiedRestriction(
            data && typeof data === 'object' ? (data as Record<string, unknown>) : null,
          );
          navigate(EMAIL_VERIFICATION_PROFILE_PATH, { replace: true });
          return;
        }
        toastStore.show(i18n.t('auth.success.sign_in'), 'success');
        window.location.href = postAuthLandingPath(user.role);
      } else {
        const errorMessage = data.message || data.error || i18n.t('errors.auth.auth_failed');
        toastStore.show(errorMessage, 'error');
        if (data.errors) {
          const apiErrors: Record<string, string> = {};
          if (data.errors.email) apiErrors.email = data.errors.email;
          if (data.errors.password) apiErrors.password = data.errors.password;
          setErrors(apiErrors);
        }
      }
    } catch (error) {
      const isCaptchaError = error instanceof Error && /reCAPTCHA|captcha/i.test(error.message);
      toastStore.show(
        isCaptchaError
          ? i18n.t('errors.auth.captcha_failed')
          : i18n.t('errors.server_connection'),
        'error',
      );
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell
      title={i18n.t('auth.login_title')}
      headerExtra={
        sessionExpiredBanner ? (
          <div role="alert" className="auth-reveal auth-session-banner w-full" style={{ animationDelay: '0.1s' }}>
            {sessionExpiredBanner}
          </div>
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} noValidate className="space-y-4">
        <div>
          <input
            type="email"
            className={`fld ${errors.email ? 'fld-error' : ''}`}
            placeholder={i18n.t('auth.work_email_placeholder')}
            autoComplete="email"
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              clearError('email');
            }}
          />
          {errors.email && <div className="auth-field-error">{errors.email}</div>}
        </div>

        <div>
          <div className="relative">
            <input
              id="pw"
              type={showPassword ? 'text' : 'password'}
              className={`fld pr-12 ${errors.password ? 'fld-error' : ''}`}
              placeholder={i18n.t('auth.password.label')}
              autoComplete="current-password"
              value={password}
              onChange={(ev) => {
                setPassword(ev.target.value);
                clearError('password');
              }}
            />
            <PasswordToggle visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />
          </div>
          {errors.password && <div className="auth-field-error">{errors.password}</div>}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-grad w-full h-[52px] rounded-2xl font-bold text-white shadow-xl shadow-brand/30"
          >
            {isLoading ? i18n.t('auth.loading.sign_in') : i18n.t('auth.login_title')}
          </button>
          <RecaptchaNotice />
        </div>
      </form>

      <div className="mt-5 text-center text-sm text-slate-400 space-y-1.5">
        <div>
          {i18n.t('auth.register.cta')}?{' '}
          <Link to="/register" className="text-brand2 font-semibold hover:underline">
            {i18n.t('auth.sign_up')}
          </Link>
        </div>
        <div>
          {i18n.t('auth.password.forgot')}?{' '}
          <Link to="/reset" className="text-brand2 font-semibold hover:underline">
            {i18n.t('app.actions.restore')}
          </Link>
        </div>
      </div>
    </AuthPageShell>
  );
})
